// ─── أسعار A+ و B+ ───
// 4-step flow for bulk-editing prices per customer-tier:
//   step 1: pick tier  (A+ royal blue   /  B+ royal purple)
//   step 2: pick brand (vertical full-width cards in tier color)
//   step 3: pick products via checkboxes (smart Arabic search at top)
//   step 4: enter new prices
//            • A+  → cost_price  + selling_price
//            • B+  → wholesale_price only
// Click "موافقة" at step 4 → PATCH each selected product in parallel.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Search,
  Building2,
  CheckCircle2,
  Loader2,
  Circle,
  Crown,
  Sparkles,
  Award,
  Save,
} from "lucide-react";
import { smartMatch } from "../../utils/arabicSearch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Tier visual tokens
const TIERS = {
  A_plus: {
    id: "A_plus",
    label: "A+",
    title: "زبائن A+",
    color: "#1E3A5F",        // royal blue
    colorSoft: "#3B82F6",
    gradient: "linear-gradient(135deg, #1E3A5F 0%, #3B82F6 100%)",
    icon: Crown,
  },
  C_plus: {
    id: "C_plus",
    label: "C+",
    title: "زبائن C+ (وسط)",
    color: "#0891B2",        // cyan
    colorSoft: "#06B6D4",
    gradient: "linear-gradient(135deg, #0E7490 0%, #06B6D4 100%)",
    icon: Award,
  },
  B_plus: {
    id: "B_plus",
    label: "B+",
    title: "زبائن B+",
    color: "#7C3AED",        // royal purple
    colorSoft: "#A855F7",
    gradient: "linear-gradient(135deg, #4C1D95 0%, #A855F7 100%)",
    icon: Sparkles,
  },
};

// Build a haystack the smart Arabic matcher can score against.
const productHaystack = (p) =>
  `${p.name_ar || ""} ${p.name_en || p.name || ""} ${p.weight_per_piece || ""} ${p.units_per_carton || ""} ${p.category || ""}`;

// ─── Step 1: Tier Picker ───
const TierPicker = ({ onPick }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto pt-4">
    {Object.values(TIERS).map((t) => {
      const Icon = t.icon;
      return (
        <button
          key={t.id}
          type="button"
          onClick={() => onPick(t.id)}
          data-testid={`abp-tier-${t.id}`}
          className="aspect-square rounded-full text-white relative overflow-hidden shadow-2xl active:scale-95 transition-transform"
          style={{ background: t.gradient }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <Icon className="w-12 h-12 opacity-90" />
            <span className="font-black text-5xl" dir="ltr">{t.label}</span>
            <span className="text-sm font-bold opacity-90">{t.title}</span>
          </div>
          {/* Decorative ring */}
          <div className="absolute inset-3 rounded-full border-4 border-white/20 pointer-events-none" />
        </button>
      );
    })}
  </div>
);

// ─── Step 2: Brand Picker ───
const BrandPicker = ({ tier, brands, onPick }) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return brands;
    return brands.filter((b) => smartMatch(`${b.name_ar || ""} ${b.name_en || ""}`, q));
  }, [brands, q]);
  return (
    <div className="space-y-3" data-testid="abp-brand-list">
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="abp-brand-search"
          placeholder="بحث ذكي عن شركة..."
          className="w-full pr-10 pl-4 py-3 rounded-2xl border-2 text-sm focus:outline-none"
          style={{ borderColor: tier.color, background: "#FAFAFA" }}
        />
      </div>
      {filtered.length === 0 ? (
        <p className="text-center text-[#5C6E64] py-8 text-sm">لا توجد شركات</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => onPick(b)}
              data-testid={`abp-brand-${b.id}`}
              className="w-full text-right px-5 py-4 rounded-2xl text-white shadow-md hover:brightness-110 active:scale-[0.99] transition-all flex items-center justify-between gap-3"
              style={{ background: tier.gradient }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-white/20 rounded-full p-2 shrink-0">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-black text-lg truncate">{b.name_ar}</p>
                  {b.name_en && <p className="text-xs opacity-80 truncate" dir="ltr">{b.name_en}</p>}
                </div>
              </div>
              <div className="bg-white/25 rounded-full px-3 py-1.5 text-xs font-bold shrink-0" dir="ltr">
                {b.product_count} منتج
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Step 3: Product Picker with smart search + multi-select ───
const ProductPicker = ({ tier, brand, products, onConfirm }) => {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState({}); // {id: product}

  const filtered = useMemo(() => {
    if (!q.trim()) return products;
    return products.filter((p) => smartMatch(productHaystack(p), q));
  }, [products, q]);

  const toggle = (p) =>
    setPicked((d) => {
      const n = { ...d };
      if (n[p.id]) delete n[p.id]; else n[p.id] = p;
      return n;
    });

  const selectedCount = Object.keys(picked).length;

  return (
    <div className="space-y-3" data-testid="abp-product-list">
      <div className="rounded-2xl p-3 text-white" style={{ background: tier.gradient }}>
        <p className="text-xs opacity-90">الشركة المختارة</p>
        <p className="font-black text-lg">{brand.name_ar}</p>
      </div>

      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="abp-product-search"
          placeholder="بحث ذكي... (مثال: كبة، نص، ألف، كيلو)"
          className="w-full pr-10 pl-4 py-3 rounded-2xl border-2 text-sm focus:outline-none"
          style={{ borderColor: tier.color, background: "#FAFAFA" }}
          autoFocus
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-center text-[#5C6E64] py-8 text-sm">لا توجد منتجات</p>
      ) : (
        <div className="space-y-1.5 max-h-[460px] overflow-y-auto pr-1">
          {filtered.map((p) => {
            const sel = !!picked[p.id];
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p)}
                data-testid={`abp-product-${p.id}`}
                className={`w-full text-right px-3 py-2 rounded-xl border-2 flex items-center justify-between gap-3 transition-all ${
                  sel ? "shadow-md" : "bg-white border-[#E4EBE6] hover:border-[#3B82F6]/40"
                }`}
                style={sel ? { background: `${tier.color}10`, borderColor: tier.color } : undefined}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {sel ? (
                    <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: tier.color }} />
                  ) : (
                    <Circle className="w-5 h-5 text-[#BAE6FD] shrink-0" />
                  )}
                  {/* Product thumbnail (image with fallback initials box) */}
                  {p.image_path ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${p.image_path}`}
                      alt={p.name_ar}
                      className="w-10 h-10 rounded-lg object-cover bg-[#F3F6F4] shrink-0"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-[#F3F6F4] flex items-center justify-center shrink-0">
                      <span className="text-[10px] text-[#5C6E64] font-bold">{(p.name_ar || "—").slice(0, 2)}</span>
                    </div>
                  )}
                  <div className="min-w-0 text-right flex-1">
                    <p className="font-bold text-sm truncate" style={{ color: sel ? tier.color : "#1E3A5F" }}>
                      {p.name_ar}
                    </p>
                    <p className="text-[10px] text-[#5C6E64] flex gap-2" dir="ltr">
                      {p.weight_per_piece && <span>{p.weight_per_piece}</span>}
                      {p.units_per_carton ? <span>· {p.units_per_carton}/كرتون</span> : null}
                    </p>
                  </div>
                </div>
                <div className="text-left shrink-0">
                  {tier.id === "A_plus" ? (
                    <>
                      <p className="text-[10px] text-[#5C6E64]">الشراء</p>
                      <p className="font-bold text-xs" dir="ltr">{fmt(p.cost_price)}</p>
                      <p className="text-[10px] text-[#5C6E64]">البيع</p>
                      <p className="font-bold text-xs" dir="ltr">{fmt(p.price)}</p>
                    </>
                  ) : tier.id === "C_plus" ? (
                    <>
                      <p className="text-[10px] text-[#5C6E64]">سعر C+ الحالي</p>
                      <p className="font-bold text-sm" dir="ltr" style={{ color: TIERS.C_plus.color }}>
                        {p.c_plus_price ? fmt(p.c_plus_price) : "—"}
                      </p>
                      <p className="text-[10px] text-[#5C6E64] mt-1">A+ (للمقارنة)</p>
                      <p className="text-[10px]" dir="ltr">{fmt(p.price)}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-[10px] text-[#5C6E64]">سعر B+ الحالي</p>
                      <p className="font-bold text-sm" dir="ltr" style={{ color: TIERS.B_plus.color }}>
                        {p.wholesale_price ? fmt(p.wholesale_price) : "—"}
                      </p>
                      <p className="text-[10px] text-[#5C6E64] mt-1">سعر A+ (للمقارنة)</p>
                      <p className="text-[10px]" dir="ltr">{fmt(p.price)}</p>
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={() => onConfirm(Object.values(picked))}
        disabled={selectedCount === 0}
        data-testid="abp-confirm-btn"
        className="w-full py-3 rounded-2xl text-white font-bold text-base shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: tier.gradient }}
      >
        موافقة على {selectedCount} منتج → تعديل الأسعار
      </button>
    </div>
  );
};

// ─── Step 4: Bulk price editor ───
const PriceEditor = ({ tier, selectedProducts, onDone, onCancel }) => {
  // Local draft keyed by product_id; A+ → {cost, sell}; C+ → {c_plus}; B+ → {wholesale}.
  const [drafts, setDrafts] = useState(() => {
    const d = {};
    selectedProducts.forEach((p) => {
      if (tier.id === "A_plus") d[p.id] = { cost: String(p.cost_price ?? ""), sell: String(p.price ?? "") };
      else if (tier.id === "C_plus") d[p.id] = { c_plus: String(p.c_plus_price ?? "") };
      else d[p.id] = { wholesale: String(p.wholesale_price ?? "") };
    });
    return d;
  });
  const [busy, setBusy] = useState(false);

  const setField = (pid, key, val) =>
    setDrafts((d) => ({ ...d, [pid]: { ...d[pid], [key]: val } }));

  const handleSave = async () => {
    setBusy(true);
    let okCount = 0, failCount = 0;
    for (const p of selectedProducts) {
      const d = drafts[p.id] || {};
      const body = {};
      if (tier.id === "A_plus") {
        if (d.cost !== "" && !isNaN(Number(d.cost))) body.cost_price = Number(d.cost);
        if (d.sell !== "" && !isNaN(Number(d.sell))) body.price = Number(d.sell);
      } else if (tier.id === "C_plus") {
        // C+ — empty string clears the c_plus_price field (falls back to base price)
        body.c_plus_price = d.c_plus === "" ? null : Number(d.c_plus);
      } else {
        // B+ — empty string clears the field (so wholesale_price=null)
        body.wholesale_price = d.wholesale === "" ? null : Number(d.wholesale);
      }
      if (Object.keys(body).length === 0) continue;
      try {
        await axios.patch(`${API}/admin/products/${p.id}`, body);
        okCount += 1;
      } catch {
        failCount += 1;
      }
    }
    setBusy(false);
    if (failCount === 0) toast.success(`تم تحديث ${okCount} منتج`);
    else toast.error(`نجح ${okCount} وفشل ${failCount}`);
    onDone();
  };

  return (
    <div className="space-y-3" data-testid="abp-editor">
      <div className="rounded-2xl p-3 text-white" style={{ background: tier.gradient }}>
        <p className="text-xs opacity-90">تعديل أسعار</p>
        <p className="font-black text-lg" dir="ltr">{selectedProducts.length} منتج · {tier.label}</p>
      </div>
      <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
        {selectedProducts.map((p) => {
          const d = drafts[p.id] || {};
          // Live "difference" between A+ price and the entered tier price
          const tierPriceNum = Number(d.wholesale ?? d.c_plus ?? 0);
          const aplus = Number(p.price || 0);
          const diff = tierPriceNum > 0 && aplus > 0 ? aplus - tierPriceNum : null;
          const diffPct = diff != null && aplus > 0 ? (diff / aplus) * 100 : null;
          return (
            <div
              key={p.id}
              className="bg-white rounded-2xl p-3 border-2 space-y-2"
              style={{ borderColor: `${tier.color}40` }}
            >
              {/* Product header — IMAGE + NAME (user request) */}
              <div className="flex items-center gap-2.5">
                {p.image_path ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${p.image_path}`}
                    alt={p.name_ar}
                    className="w-12 h-12 rounded-xl object-cover bg-[#F3F6F4] shrink-0 border"
                    style={{ borderColor: `${tier.color}30` }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-[#F3F6F4] flex items-center justify-center shrink-0 border" style={{ borderColor: `${tier.color}30` }}>
                    <span className="text-[11px] text-[#5C6E64] font-bold">{(p.name_ar || "—").slice(0, 2)}</span>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-[#1E3A5F] leading-tight">{p.name_ar}</p>
                  <p className="text-[10px] text-[#5C6E64] flex gap-2" dir="ltr">
                    {p.weight_per_piece && <span>{p.weight_per_piece}</span>}
                    {p.units_per_carton ? <span>· {p.units_per_carton}/كرتون</span> : null}
                  </p>
                </div>
              </div>
              {tier.id === "A_plus" ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[10px] text-[#5C6E64] mb-0.5">سعر الشراء</p>
                    <input
                      type="number"
                      value={d.cost}
                      onChange={(e) => setField(p.id, "cost", e.target.value)}
                      data-testid={`abp-cost-${p.id}`}
                      className="w-full px-3 py-2 rounded-xl border-2 text-sm font-bold"
                      style={{ borderColor: tier.color, color: "#1E3A5F" }}
                      dir="ltr"
                    />
                  </div>
                  <div>
                    <p className="text-[10px] text-[#5C6E64] mb-0.5">سعر البيع</p>
                    <input
                      type="number"
                      value={d.sell}
                      onChange={(e) => setField(p.id, "sell", e.target.value)}
                      data-testid={`abp-sell-${p.id}`}
                      className="w-full px-3 py-2 rounded-xl border-2 text-sm font-bold"
                      style={{ borderColor: tier.color, color: "#1E3A5F" }}
                      dir="ltr"
                    />
                  </div>
                </div>
              ) : tier.id === "C_plus" ? (
                <div className="space-y-2">
                  <div>
                    <p className="text-[10px] font-bold mb-0.5" style={{ color: tier.color }}>سعر C+ الجديد ★</p>
                    <input
                      type="number"
                      value={d.c_plus}
                      onChange={(e) => setField(p.id, "c_plus", e.target.value)}
                      data-testid={`abp-cplus-${p.id}`}
                      placeholder="اكتب سعر C+ هنا"
                      className="w-full px-3 py-2.5 rounded-xl border-2 text-base font-black"
                      style={{ borderColor: tier.color, color: tier.color, background: `${tier.color}08` }}
                      dir="ltr"
                      autoFocus
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 text-center">
                    <div className="rounded-lg py-1.5 px-2" style={{ background: "#EFF6FF" }}>
                      <p className="text-[9px] text-[#1E3A5F]/70">سعر A+</p>
                      <p className="font-black text-sm" style={{ color: TIERS.A_plus.color }} dir="ltr">{fmt(p.price)}</p>
                    </div>
                    <div className="rounded-lg py-1.5 px-2" style={{ background: "#F5F3FF" }}>
                      <p className="text-[9px]" style={{ color: TIERS.B_plus.color }}>سعر B+</p>
                      <p className="font-black text-sm" style={{ color: TIERS.B_plus.color }} dir="ltr">
                        {p.wholesale_price ? fmt(p.wholesale_price) : "—"}
                      </p>
                    </div>
                    <div className="rounded-lg py-1.5 px-2" style={{ background: diff != null ? `${tier.color}15` : "#F3F6F4" }}>
                      <p className="text-[9px]" style={{ color: tier.color }}>الفرق عن A+</p>
                      {diff != null ? (
                        <p className="font-black text-sm" style={{ color: tier.color }} dir="ltr">
                          {fmt(diff)}{diffPct != null && <span className="text-[9px] opacity-70"> ({diffPct.toFixed(1)}%)</span>}
                        </p>
                      ) : (
                        <p className="font-bold text-sm text-[#5C6E64]">—</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div>
                    <p className="text-[10px] font-bold mb-0.5" style={{ color: tier.color }}>سعر B+ الجديد ★</p>
                    <input
                      type="number"
                      value={d.wholesale}
                      onChange={(e) => setField(p.id, "wholesale", e.target.value)}
                      data-testid={`abp-wholesale-${p.id}`}
                      placeholder="اكتب سعر B+ هنا"
                      className="w-full px-3 py-2.5 rounded-xl border-2 text-base font-black"
                      style={{ borderColor: tier.color, color: tier.color, background: `${tier.color}08` }}
                      dir="ltr"
                      autoFocus
                    />
                  </div>
                  {/* Comparison: A+ price + cost + LIVE difference — original tier colors */}
                  <div className="grid grid-cols-3 gap-1.5 text-center">
                    <div className="rounded-lg py-1.5 px-2" style={{ background: "#EFF6FF" }}>
                      <p className="text-[9px] text-[#1E3A5F]/70">سعر A+</p>
                      <p className="font-black text-sm" style={{ color: TIERS.A_plus.color }} dir="ltr">{fmt(p.price)}</p>
                    </div>
                    <div className="rounded-lg py-1.5 px-2" style={{ background: "#FFF7ED" }}>
                      <p className="text-[9px] text-[#92400e]/70">الشراء</p>
                      <p className="font-black text-sm" style={{ color: "#92400e" }} dir="ltr">{fmt(p.cost_price)}</p>
                    </div>
                    <div className="rounded-lg py-1.5 px-2" style={{ background: diff != null ? `${tier.color}15` : "#F3F6F4" }}>
                      <p className="text-[9px]" style={{ color: tier.color }}>الفرق</p>
                      {diff != null ? (
                        <p className="font-black text-sm" style={{ color: tier.color }} dir="ltr">
                          {fmt(diff)}{diffPct != null && <span className="text-[9px] opacity-70"> ({diffPct.toFixed(1)}%)</span>}
                        </p>
                      ) : (
                        <p className="font-bold text-sm text-[#5C6E64]">—</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid="abp-cancel-btn"
          className="flex-1 py-3 rounded-2xl bg-[#F3F6F4] text-[#5C6E64] font-bold"
        >
          إلغاء
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={busy}
          data-testid="abp-save-btn"
          className="flex-[2] py-3 rounded-2xl text-white font-bold shadow-lg flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: tier.gradient }}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          حفظ التغييرات
        </button>
      </div>
    </div>
  );
};

// ─── Main tab ───
const AbPricesTab = () => {
  const [tierId, setTierId] = useState(null);   // null | "A_plus" | "B_plus"
  const [brand, setBrand] = useState(null);     // brand doc
  const [selected, setSelected] = useState([]); // product[] picked for editing
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const tier = tierId ? TIERS[tierId] : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/brands`),
        axios.get(`${API}/admin/products`),
      ]);
      setBrands(bRes.data || []);
      setProducts(pRes.data || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const brandProducts = useMemo(
    () => products.filter((p) => brand && p.brand_id === brand.id),
    [products, brand]
  );

  const goBack = () => {
    if (selected.length > 0) { setSelected([]); return; }
    if (brand) { setBrand(null); return; }
    if (tierId) { setTierId(null); return; }
  };

  // Header label tracks the current step
  let stepLabel = "اختر فئة الزبائن";
  if (tier && !brand) stepLabel = `${tier.title} — اختر الشركة`;
  else if (tier && brand && selected.length === 0) stepLabel = `${tier.title} — ${brand.name_ar} — اختر المنتجات`;
  else if (tier && brand && selected.length > 0) stepLabel = `تعديل أسعار ${tier.label}`;

  return (
    <div className="space-y-3" dir="rtl" data-testid="ab-prices-tab">
      {/* Sticky header — title + back */}
      <div
        className="rounded-2xl p-3 flex items-center justify-between gap-2 text-white shadow"
        style={{ background: tier ? tier.gradient : "linear-gradient(135deg, #1E3A5F 0%, #3B82F6 50%, #7C3AED 100%)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {(tierId || brand) && (
            <button
              type="button"
              onClick={goBack}
              data-testid="abp-back-btn"
              className="bg-white/20 hover:bg-white/30 rounded-full p-1.5 shrink-0"
              aria-label="رجوع"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <div className="min-w-0">
            <p className="text-[10px] opacity-80">أسعار A+ و B+</p>
            <p className="font-black text-base truncate">{stepLabel}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-center py-12 text-[#5C6E64]">جاري التحميل...</p>
      ) : !tierId ? (
        <TierPicker onPick={setTierId} />
      ) : !brand ? (
        <BrandPicker tier={tier} brands={brands} onPick={setBrand} />
      ) : selected.length === 0 ? (
        <ProductPicker
          tier={tier}
          brand={brand}
          products={brandProducts}
          onConfirm={(items) => {
            if (items.length === 0) return;
            setSelected(items);
          }}
        />
      ) : (
        <PriceEditor
          tier={tier}
          selectedProducts={selected}
          onCancel={() => setSelected([])}
          onDone={async () => {
            setSelected([]);
            await fetchData();
          }}
        />
      )}
    </div>
  );
};

export default AbPricesTab;
